/**
 * AI Agent Service
 *
 * Purpose: API client for AI Agent Manager CRUD, override, revert, restore, and the
 *          curated skills/tool-calls options endpoint.
 * Architecture: Thin wrapper over apiService; mirrors workflowService shape.
 * Author: Development Team
 * Created: 2026-05-23
 */

import { apiService } from './api';
import { AiSkillOption } from './aiSkillService';

export type { AiSkillOption };

// =============================================================================
// TYPES
// =============================================================================

export interface ScopeInfo {
  scope: 'GLOBAL' | 'Overridden' | 'LOCAL';
  is_override: boolean;
  has_global_parent: boolean;
}

export interface UserInfo {
  email: string;
  user_id: string;
  first_name?: string;
  last_name?: string;
}

export type AIAgentStatus = 'active' | 'disabled' | 'deleted';
export type AccessLevel = '0' | '1' | '2' | '3';

/** How an agent runs (ORCH-286 §5.2). */
export type AgentKind = 'producer' | 'reviewer';

/** Typed shape of `ai_agent_definitions.settings` (ORCH-286 §5.2). */
export interface AgentSettings {
  kind: AgentKind;
  web_search: boolean;
  /** Producer only. 0-100. */
  max_tool_calls: number;
  /** Heading when merged in a parallel step; default = label. */
  section_heading: string | null;
  /** Telemetry/event phase for a fan-out sub-agent. Pattern: ^[a-z][a-z0-9_]*$ */
  phase_label: string | null;
  /** Reviewer only: verdict text when the model returns bad JSON. */
  invalid_output_feedback: string | null;
}

export const DEFAULT_AGENT_SETTINGS: AgentSettings = {
  kind: 'producer',
  web_search: false,
  max_tool_calls: 10,
  section_heading: null,
  phase_label: null,
  invalid_output_feedback: null,
};

export interface AIAgent {
  ai_agent_id: string;
  tenant_id: string;
  label: string;
  skills: string[];
  tool_calls: string[];
  system_prompt: string;
  settings: AgentSettings;
  status: AIAgentStatus;
  access: AccessLevel;
  scope_info?: ScopeInfo;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  created_by_user?: UserInfo;
  updated_by_user?: UserInfo;
}

export interface AIAgentCreatePayload {
  ai_agent_id?: string;
  tenant_id: string;
  label: string;
  skills?: string[];
  tool_calls?: string[];
  system_prompt?: string;
  settings?: AgentSettings;
  status?: AIAgentStatus;
  access?: AccessLevel;
}

export interface AIAgentUpdatePayload {
  label?: string;
  skills?: string[];
  tool_calls?: string[];
  system_prompt?: string;
  settings?: AgentSettings;
  status?: AIAgentStatus;
  access?: AccessLevel;
}

export interface AIAgentListParams {
  tenant_id?: string;
  status?: AIAgentStatus;
  label_contains?: string;
}

export interface AIAgentOptions {
  /** Admin-managed skills from the ai_skills table ({id, name}); agent stores ids. */
  skills: AiSkillOption[];
  tool_calls: string[];
}

export interface AIAgentConflictRecord {
  ai_agent_id: string;
  tenant_id: string;
  label: string;
  scope: string;
  /** Status of the row holding the ID/name — 'deleted' means it can be restored (ORCH-363). */
  status?: AIAgentStatus;
}

export interface AIAgentConflict {
  error: 'duplicate_name' | 'duplicate_id';
  message: string;
  conflicting_record: AIAgentConflictRecord;
}

// =============================================================================
// SERVICE
// =============================================================================

class AIAgentServiceImpl {
  private baseUrl = '/metadata/ai-agents';

  async listAIAgents(params: AIAgentListParams = {}): Promise<AIAgent[]> {
    const response = await apiService.api.get<AIAgent[]>(this.baseUrl, { params });
    return response.data;
  }

  async getAIAgent(aiAgentId: string, tenantId: string): Promise<AIAgent> {
    const response = await apiService.api.get<AIAgent>(`${this.baseUrl}/${aiAgentId}/${tenantId}`);
    return response.data;
  }

  async createAIAgent(data: AIAgentCreatePayload): Promise<AIAgent> {
    const response = await apiService.api.post<AIAgent>(this.baseUrl, data);
    return response.data;
  }

  async updateAIAgent(
    aiAgentId: string,
    tenantId: string,
    data: AIAgentUpdatePayload,
    currentTenantId?: string
  ): Promise<AIAgent> {
    const params = currentTenantId ? { current_tenant_id: currentTenantId } : {};
    const response = await apiService.api.put<AIAgent>(
      `${this.baseUrl}/${aiAgentId}/${tenantId}`,
      data,
      { params }
    );
    return response.data;
  }

  async deleteAIAgent(aiAgentId: string, tenantId: string): Promise<void> {
    await apiService.api.delete(`${this.baseUrl}/${aiAgentId}/${tenantId}`);
  }

  /** Undo a soft delete — the same row becomes active again. Not a revert. */
  async restoreAIAgent(aiAgentId: string, tenantId: string): Promise<AIAgent> {
    const response = await apiService.api.post<AIAgent>(
      `${this.baseUrl}/${aiAgentId}/${tenantId}/restore`
    );
    return response.data;
  }

  async revertAIAgent(aiAgentId: string, tenantId: string): Promise<void> {
    await apiService.api.delete(`${this.baseUrl}/${aiAgentId}/${tenantId}/revert`);
  }

  async getOptions(): Promise<AIAgentOptions> {
    const response = await apiService.api.get<AIAgentOptions>(`${this.baseUrl}/options`);
    return response.data;
  }

  /**
   * Extract structured 409 duplicate-name detail from an Axios error.
   * Returns null when the error is not a 409 with the expected shape.
   */
  extractConflict(error: unknown): AIAgentConflict | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const err = error as any;
    if (!err?.response || err.response.status !== 409) {
      return null;
    }
    const detail = err.response.data?.detail ?? err.response.data;
    if (
      detail &&
      typeof detail === 'object' &&
      'error' in detail &&
      'conflicting_record' in detail
    ) {
      return detail as AIAgentConflict;
    }
    return null;
  }
}

export const aiAgentService = new AIAgentServiceImpl();
export default aiAgentService;
